import { ARAC_KAPASITE_NOTU, SAFARI_YAS_NOTU } from '../../../api/src/modules/hizli-teklif/kisi-basi-hesap';
import { paketDuzenlemesiCoz, type PaketDuzenlemeTaslagi, type PaketDuzenlemeTaslaklari } from './paket-duzenleme';
/**
 * Gezi ve transfer paketi anahtarları (tüm grup için). Yalnız katalogda paketi olan destinasyonların otelleri seçiliyken
 * görünür; yoksa "bu destinasyon için katalog paketi yok" bilgisi kalır. Fiyatı bilinmeyen paket "dahil" sayılmaz.
 */
import { useId } from 'react';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { KISI_BASI_PAKETLER } from './mantik';
import type { AracSecimi, PaketFiyati, PaketKodu, YatBoyutu } from './tipler';

export interface AracGorunumu {
  readonly anahtar: AracSecimi;
  readonly etiket: string;
  /** "220 USD" gibi toplam metni. */
  readonly tutar: string;
  readonly oneri: boolean;
}

export interface PaketGorunumu {
  readonly kod: PaketKodu;
  readonly acik: boolean;
  /** "210,00 USD", "fiyat yok" ya da "fiyat alınıyor". */
  readonly fiyat: string;
  readonly fiyatYok: boolean;
  /** Çocuk fiyatı yok: "çağrı merkezi" cümlesi gösterilir (yalnız kişi başı geziler). */
  readonly cocukFiyatiYok?: boolean;
}

/** Özel yat ayarı (28.10.2026): saat sayısı ve yat boyutu. Fiyat yat başınadır. */
export interface YatGorunumu {
  readonly saat: string;
  readonly saatGecerli: boolean;
  readonly boyut: YatBoyutu | null;
  /** Saat başı fiyat metni ("156,57 USD"); fiyat henüz gelmediyse boş. */
  readonly secenekler: readonly { readonly anahtar: YatBoyutu; readonly ad: string; readonly saatBasi: string }[];
  readonly toplam: string;
  readonly saatDegistir: (saat: string) => void;
  readonly boyutDegistir: (boyut: YatBoyutu) => void;
}

export function PaketAlani({ toplamSiniriAsildi = false, fiyatlar = [], duzenlemeler = {}, duzenle, paketler, degistir, yokBilgisi, devreDisi, notlar = {}, notDegistir, araclar = [], aracSecili = null, aracDegistir, yat, safariKoltuk }: { toplamSiniriAsildi?: boolean; fiyatlar?: readonly PaketFiyati[]; duzenlemeler?: PaketDuzenlemeTaslaklari; duzenle?: (kod: PaketKodu, d: PaketDuzenlemeTaslagi | undefined) => void; paketler: readonly PaketGorunumu[]; degistir: (kod: PaketKodu, acik: boolean) => void; yokBilgisi: boolean; devreDisi: boolean; notlar?: Readonly<Partial<Record<PaketKodu, string>>>; notDegistir?: (kod: PaketKodu, not: string) => void; araclar?: readonly AracGorunumu[]; aracSecili?: AracSecimi | null; aracDegistir?: (a: AracSecimi) => void; yat?: YatGorunumu; safariKoltuk?: { bebekSayisi: number; adet: number; degistir: (adet: number) => void } }) {
  const kimlik = useId();
  return (
    <>
      {yokBilgisi ? <p className="ht-alan ht-ipucu ht-paket-yok" data-testid="ht-paket-yok">{M.paketYok}</p> : null}
      {paketler.length > 0 ? (
        <div className="ht-alan ht-paket-kap" role="group" aria-labelledby={`${kimlik}-pk`} data-testid="ht-paket-kap">
          <span id={`${kimlik}-pk`} className="ht-etiket">{M.paketBaslik}</span>
          <details className="ht-urun-secici" data-testid="ht-urun-secici" open><summary>Ürün ekle veya çıkar</summary>
          <div className="ht-paketler">
            {paketler.map((p) => (
              <button key={p.kod} type="button" role="switch" aria-checked={p.acik} className="ht-anahtar" disabled={devreDisi} onClick={() => degistir(p.kod, !p.acik)} data-testid={`ht-paket-${p.kod}`}>
                <i aria-hidden="true" />
                <span>{M.paketler[p.kod]}</span>
                {p.acik ? <small className={p.fiyatYok ? 'ht-uyari-yazi' : ''}>{p.fiyat}</small> : null}
              </button>
            ))}
          </div>
          </details>
          {paketler.some(p => p.kod === 'SAFARI_TURU' && p.acik) && safariKoltuk !== undefined && safariKoltuk.bebekSayisi > 0 ? <label className="ht-alan">Safari 0–2 yaş ek koltuk (koltuk başı 90 USD)
            <select aria-label="Safari bebek ek koltuk sayısı" disabled={devreDisi} value={safariKoltuk.adet} onChange={e => safariKoltuk.degistir(Number(e.target.value))}>
              {Array.from({ length: safariKoltuk.bebekSayisi + 1 }, (_, i) => <option key={i} value={i}>{i === 0 ? 'Ek koltuk yok (0–2 yaş ücretsiz)' : `${i} ek koltuk`}</option>)}
            </select>
          </label> : null}
          {toplamSiniriAsildi ? <p className="ht-hata-yazi" role="alert">Teklif toplamı izin verilen tutar sınırını aşıyor. Satış fiyatlarını kontrol edin.</p> : null}
          {paketler.some(p => p.acik) ? <div className="ht-paket-tablo-kap">
            <table className="ht-paket-tablo" data-testid="ht-paket-tablo">
              <thead><tr><th>Ürün / teklif başlığı</th><th>Birim satış fiyatı</th><th>4 kişilik araç toplamı</th><th>6 kişilik araç toplamı</th>{araclar.some(a => a.anahtar === 'KARISIK') ? <th>Karışık araç toplamı</th> : null}<th>Seçili toplam / not</th></tr></thead>
              <tbody>{paketler.filter(p => p.acik).map(p => {
                const f = fiyatlar.find(f => f.kod === p.kod);
                const d = duzenlemeler[p.kod];
                const para = f?.paraBirimi;
                const kapali = devreDisi || !para || !duzenle;
                const degis = (patch: Partial<PaketDuzenlemeTaslagi>) => { if (para) duzenle?.(p.kod, { paraBirimi: para, ...d, ...patch }); };
                const input = (varyant: 'KISI' | 'SAAT' | AracSecimi, minor: number | undefined) => {
                  if (minor === undefined) return <span className="ht-ipucu">Uygulanmaz</span>;
                  const deger = varyant === 'KISI' ? d?.yetiskin : varyant === 'SAAT' ? d?.saat : d?.aracToplamlari?.[varyant];
                  return <label className="ht-paket-fiyat"><span>{varyant === 'KISI' ? 'Yetişkin başı' : varyant === 'SAAT' ? 'Yat / saat' : 'Tüm araçlar'} · {para}</span>
                    <input aria-label={`${M.paketler[p.kod]} ${varyant} satış fiyatı`} data-testid={`ht-paket-fiyat-${p.kod}-${varyant}`} inputMode="decimal" value={deger ?? (minor / 100).toFixed(2)} disabled={kapali}
                      onChange={e => degis(varyant === 'KISI' ? { yetiskin: e.target.value } : varyant === 'SAAT' ? { saat: e.target.value, yatBoyutu: f?.yat?.boyut } : { aracToplamlari: { ...d?.aracToplamlari, [varyant]: e.target.value } })} />
                  </label>;
                };
                return <tr key={p.kod} data-testid={`ht-paket-satir-${p.kod}`}>
                  <td data-label="Ürün / teklif başlığı"><input aria-label={`${M.paketler[p.kod]} teklif başlığı`} data-testid={`ht-paket-ad-${p.kod}`} value={d?.ad ?? f?.ad ?? M.paketler[p.kod]} maxLength={120} disabled={kapali} onChange={e => degis({ ad: e.target.value })} />
                    <small className="ht-ipucu">{M.paketler[p.kod]}</small>
                    {d ? <button type="button" className="ht-baglanti" disabled={devreDisi} data-testid={`ht-paket-sifirla-${p.kod}`} onClick={() => duzenle?.(p.kod, undefined)}>Başlığı ve fiyatı sıfırla</button> : null}
                    {d && paketDuzenlemesiCoz(p.kod, d) === null ? <span className="ht-hata-yazi" role="alert">Başlık ve satış fiyatını kontrol edin.</span> : null}
                  </td>
                  <td data-label="Birim satış fiyatı">{f?.kisiBasi ? input('KISI', f.kisiBasiFiyat?.yetiskinMinor) : f?.saatBasi ? input('SAAT', f.yat?.saatBasiMinor) : <span className="ht-ipucu">Araç toplamı</span>}</td>
                  <td data-label="4 kişilik araç toplamı">{input('DORT', f?.secenekler?.find(s => s.anahtar === 'DORT')?.tutarMinor)}</td>
                  <td data-label="6 kişilik araç toplamı">{input('ALTI', f?.secenekler?.find(s => s.anahtar === 'ALTI')?.tutarMinor)}</td>
                  {araclar.some(a => a.anahtar === 'KARISIK') ? <td data-label="Karışık araç toplamı">{input('KARISIK', f?.secenekler?.find(s => s.anahtar === 'KARISIK')?.tutarMinor)}</td> : null}
                  <td data-label="Seçili toplam / not"><b>{p.fiyat}</b><small className="ht-ipucu">{f?.kapsam}</small><textarea aria-label={`${M.paketler[p.kod]} not`} rows={2} maxLength={600} value={notlar[p.kod] ?? ''} disabled={devreDisi} placeholder={M.paketNotIpucu} data-testid={`ht-paket-not-${p.kod}`} onChange={e => notDegistir?.(p.kod, e.target.value)} /></td>
                </tr>;
              })}</tbody>
            </table>
          </div> : null}
          {araclar.length > 0 ? (
            <div className="ht-arac" role="radiogroup" aria-label={M.aracBaslik} data-testid="ht-arac-grup">
              <span className="ht-etiket">{M.aracBaslik}</span><p className="ht-ipucu">{ARAC_KAPASITE_NOTU}</p>
              {araclar.map((a) => (
                <label key={a.anahtar} className="ht-arac-secenek">
                  <input type="radio" name={`${kimlik}-arac`} checked={aracSecili === a.anahtar} disabled={devreDisi} onChange={() => aracDegistir?.(a.anahtar)} data-testid={`ht-arac-${a.anahtar}`} />
                  <span>{a.etiket}: {a.tutar}{a.oneri ? ` (${M.aracOneri})` : ''}</span>
                </label>
              ))}
            </div>
          ) : null}
          {yat !== undefined && paketler.some((p) => p.kod === 'OZEL_YAT_TURU' && p.acik) ? (
            <fieldset className="ht-yat" data-testid="ht-yat">
              <legend>{M.yat.baslik}</legend>
              <div className="ht-alan ht-yat-saat">
                <label htmlFor={`${kimlik}-yat-saat`}>{M.yat.saat}</label>
                <input
                  id={`${kimlik}-yat-saat`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={12}
                  step={1}
                  value={yat.saat}
                  disabled={devreDisi}
                  aria-invalid={yat.saatGecerli ? undefined : 'true'}
                  aria-describedby={`${kimlik}-yat-saat-not`}
                  data-testid="ht-yat-saat"
                  onChange={(e) => yat.saatDegistir(e.target.value)}
                />
                <span id={`${kimlik}-yat-saat-not`} className={yat.saatGecerli ? 'ht-ipucu' : 'ht-hata-yazi'}>{yat.saatGecerli ? M.yat.saatIpucu : M.yat.saatHata}</span>
              </div>
              <div className="ht-alan" role="radiogroup" aria-label={M.yat.boyut}>
                <span className="ht-etiket">{M.yat.boyut}</span>
                {yat.secenekler.map((b) => (
                  <label key={b.anahtar} className="ht-arac-secenek">
                    <input type="radio" name={`${kimlik}-yat-boyut`} checked={yat.boyut === b.anahtar} disabled={devreDisi} onChange={() => yat.boyutDegistir(b.anahtar)} data-testid={`ht-yat-${b.anahtar}`} />
                    <span>{b.ad}{b.saatBasi === '' ? '' : `: ${M.yat.saatBasi(b.saatBasi)}`}</span>
                  </label>
                ))}
              </div>
              <p className="ht-ipucu ht-paket-notu" data-testid="ht-yat-notu">{M.yat.not} {M.paketOperasyonNotu}.{yat.toplam === '' ? '' : ` ${yat.toplam}`}</p>
            </fieldset>
          ) : null}
          {paketler.filter((p) => p.acik && KISI_BASI_PAKETLER.includes(p.kod)).map((p) => (
            <p key={p.kod} className="ht-ipucu ht-paket-notu" data-testid={`ht-paket-notu-${p.kod}`}>
              <b>{M.paketler[p.kod]}:</b> {p.kod === 'SAFARI_TURU' ? SAFARI_YAS_NOTU : M.paketKisiBasi + '.'} {M.paketOperasyonNotu}.{p.cocukFiyatiYok === true ? ` ${M.paketCocukCagri}.` : ''}
            </p>
          ))}

        </div>
      ) : null}
    </>
  );
}
